import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alertwhale/ui'
import { Send, Loader2, AlertCircle } from 'lucide-react'
import { useApplications } from '@/hooks/useApplications'
import { useSendNotification } from '@/hooks/useNotifications'

interface SendNotificationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SendNotificationDialog({ open, onOpenChange }: SendNotificationDialogProps) {
  const { data: applications, isLoading: appsLoading } = useApplications()
  const sendNotification = useSendNotification()

  const [formData, setFormData] = useState({
    applicationId: '',
    title: '',
    body: '',
    event: 'notification',
    priority: 'default' as 'default' | 'high' | 'low',
    userId: '',
  })
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.applicationId) {
      setError('Please select an application')
      return
    }
    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }

    try {
      await sendNotification.mutateAsync({
        application_id: formData.applicationId,
        audience: formData.userId ? { user_id: formData.userId } : { all: true },
        event: formData.event,
        notification: {
          title: formData.title,
          body: formData.body || undefined,
        },
        priority: formData.priority,
      })

      // Reset form and close dialog
      setFormData({
        applicationId: '',
        title: '',
        body: '',
        event: 'notification',
        priority: 'default',
        userId: '',
      })
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send notification')
    }
  }

  const apps = applications || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Send Notification</DialogTitle>
          <DialogDescription>
            Send a push notification to your users
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          {error && (
            <div className="flex items-center gap-3 rounded-xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="application">
              Application <span className="text-red-400">*</span>
            </Label>
            <Select
              value={formData.applicationId}
              onValueChange={(value) => setFormData({ ...formData, applicationId: value })}
            >
              <SelectTrigger id="application">
                <SelectValue placeholder={appsLoading ? 'Loading...' : 'Select an application'} />
              </SelectTrigger>
              <SelectContent>
                {apps.map((app) => (
                  <SelectItem key={app.id} value={app.id}>
                    {app.name}
                  </SelectItem>
                ))}
                {apps.length === 0 && !appsLoading && (
                  <div className="px-3 py-4 text-sm text-slate-500 text-center">
                    No applications found. Create one first.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-red-400">*</span>
            </Label>
            <Input
              id="title"
              placeholder="Notification title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Body</Label>
            <Input
              id="body"
              placeholder="Notification body (optional)"
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event">Event Type</Label>
              <Input
                id="event"
                placeholder="e.g. order.shipped"
                value={formData.event}
                onChange={(e) => setFormData({ ...formData, event: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: 'default' | 'high' | 'low') =>
                  setFormData({ ...formData, priority: value })
                }
              >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="userId">User ID</Label>
            <Input
              id="userId"
              placeholder="Leave empty to send to all users"
              value={formData.userId}
              onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
            />
            <p className="text-xs text-slate-500">
              Specify a user ID to target a specific user, or leave empty to broadcast to all.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={sendNotification.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={sendNotification.isPending || apps.length === 0}
            >
              {sendNotification.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Notification
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
